import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import reportClientError from "../utils/clientTelemetry";

const SCRIPT_ID = "lusuku-google-maps";
const CALLBACK_NAME = "__lusukuGoogleMapsReady";
const LIBRARIES = ["places"];
let loaderPromise = null;

function buildMapsUrl(apiKey) {
  const params = new URLSearchParams({
    key: apiKey,
    libraries: LIBRARIES.join(","),
    v: "weekly",
    loading: "async",
    callback: CALLBACK_NAME,
  });
  return `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
}

function removeFailedScript() {
  const script = document.getElementById(SCRIPT_ID);
  if (script) script.remove();
  try {
    delete window[CALLBACK_NAME];
  } catch (_) {
    window[CALLBACK_NAME] = undefined;
  }
}

function loadGoogleMaps(apiKey, retry = false) {
  if (typeof window === "undefined") return Promise.reject(new Error("Maps requires a browser"));
  if (window.google?.maps?.places) return Promise.resolve(window.google);

  if (retry) {
    loaderPromise = null;
    removeFailedScript();
  }
  if (loaderPromise) return loaderPromise;
  if (!apiKey) return Promise.reject(new Error("Google Maps is not configured"));

  loaderPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google), { once: true });
      existing.addEventListener("error", () => reject(new Error("Google Maps failed to load")), {
        once: true,
      });
      return;
    }

    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = buildMapsUrl(apiKey);
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      loaderPromise = null;
      reject(new Error("Google Maps failed to load"));
    };
    window[CALLBACK_NAME] = () => {
      try {
        delete window[CALLBACK_NAME];
      } catch (_) {
        window[CALLBACK_NAME] = undefined;
      }
      resolve(window.google);
    };
    document.head.appendChild(script);
  });

  return loaderPromise;
}

const GoogleMapsContext = createContext({
  isLoaded: false,
  isLoading: true,
  error: null,
  retry: () => {},
});

export function GoogleMapsProvider({ children }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY_WEBAPP;
  const [state, setState] = useState({
    isLoaded: Boolean(typeof window !== "undefined" && window.google?.maps?.places),
    isLoading: true,
    error: null,
  });

  const startLoading = useCallback(
    (retry = false) => {
      setState((current) => ({ ...current, isLoading: true, error: null }));
      return loadGoogleMaps(apiKey, retry)
        .then(() => setState({ isLoaded: true, isLoading: false, error: null }))
        .catch((error) => {
          reportClientError(error, { area: "google_maps_load" });
          setState({ isLoaded: false, isLoading: false, error });
        });
    },
    [apiKey]
  );

  useEffect(() => {
    startLoading();
  }, [startLoading]);

  const value = useMemo(
    () => ({
      ...state,
      retry: () => startLoading(true),
    }),
    [state, startLoading]
  );

  return <GoogleMapsContext.Provider value={value}>{children}</GoogleMapsContext.Provider>;
}

GoogleMapsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useGoogleMaps() {
  return useContext(GoogleMapsContext);
}

export default GoogleMapsContext;
